/** SLA-sorted review queues (doc 11 §2). */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Localized } from '@servicesai/api-client';
// The channel vocabulary is shared with the platform rather than restated
// here. This page and CrmCaseCitizen each kept their own copy, and both still
// listed `voice` as a channel long after it became a modality.
import { channelLabel } from '@servicesai/shared';
import {
  Chip,
  DataTable,
  FilterBar,
  GlassInput,
  StatusPill,
  formatDate,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile } from '../components/elevated/kit.js';

const COPY = {
  openCases: { en: 'Open cases', ar: 'حالات مفتوحة' },
  openCasesHint: { en: 'Across active review stages', ar: 'عبر مراحل المراجعة النشطة' },
  overdueHint: { en: 'Past the SLA due date', ar: 'تجاوزت الموعد النهائي' },
  dueSoon: { en: 'Due within 24h', ar: 'تستحق خلال ٢٤ ساعة' },
  dueSoonHint: { en: 'SLA breach risk', ar: 'معرضة لتجاوز الموعد النهائي' },
};

interface QueueItem {
  id: string;
  stage: string;
  status: string;
  due_at: string | null;
  reference: string;
  state: string;
  lang: string;
  channel: string;
  sla_due_at: string | null;
  submitted_at: string | null;
  service_slug: string;
  service_name: Localized;
  assignee_id: string | null;
  priority_score: number | null;
  housing_priority?: {
    score?: number;
    band?: 'low' | 'medium' | 'high' | 'urgent';
  } | null;
}

const STATUS_FILTERS = ['', 'queued', 'in_progress', 'returned', 'approved'] as const;

const STATUS_LABEL: Record<string, Localized> = {
  queued: { en: 'Queued', ar: 'في الانتظار' },
  in_progress: { en: 'In progress', ar: 'قيد المراجعة' },
  returned: { en: 'Returned', ar: 'أُعيد' },
  approved: { en: 'Stage done', ar: 'اكتملت المرحلة' },
};

type SavedView = 'all' | 'risk' | 'mine' | 'unassigned';

type Density = 'comfort' | 'compact';

const DENSITY_KEY = 'sai.console.queueDensity';

function readDensity(): Density {
  try {
    return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfort';
  } catch {
    return 'comfort';
  }
}

function isOverdue(item: QueueItem): boolean {
  return item.due_at !== null && new Date(item.due_at).getTime() < Date.now() && item.status !== 'approved';
}

export function QueuesPage() {
  const { l, lang } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(false);
  const [view, setView] = useState<SavedView>('all');
  const [meId, setMeId] = useState<string | null>(null);
  // Deep-linked queue filter (?queue=<stage>) from the dashboard heat strip.
  const [stageFilter, setStageFilter] = useState(() => searchParams.get('queue') ?? '');
  const [density, setDensity] = useState<Density>(readDensity);

  useEffect(() => {
    setError(false);
    admin()
      .queues(status ? { status } : undefined)
      .then((res) => setItems(res.items as unknown as QueueItem[]))
      .catch(() => setError(true));
  }, [status]);

  useEffect(() => {
    admin()
      .me()
      .then((me) => setMeId(me.id))
      .catch(() => undefined);
  }, []);

  const applyDensity = (next: Density) => {
    setDensity(next);
    try {
      localStorage.setItem(DENSITY_KEY, next);
    } catch {
      /* storage unavailable */
    }
  };

  const viewFiltered = (items ?? []).filter((item) => {
    if (view === 'risk' && !isOverdue(item)) return false;
    if (view === 'mine' && (meId === null || item.assignee_id !== meId)) return false;
    if (view === 'unassigned' && item.assignee_id !== null) return false;
    if (stageFilter && item.stage !== stageFilter) return false;
    return true;
  });

  const filtered = viewFiltered.filter((item) => {
    const value = `${item.reference} ${item.service_slug} ${l(item.service_name)} ${item.stage}`.toLocaleLowerCase();
    return value.includes(search.trim().toLocaleLowerCase());
  });

  const openCount = (items ?? []).filter((item) => item.status !== 'approved').length;
  const overdueCount = (items ?? []).filter(isOverdue).length;
  const mineCount = meId === null ? 0 : (items ?? []).filter((item) => item.assignee_id === meId).length;
  const unassignedCount = (items ?? []).filter((item) => item.assignee_id === null).length;
  const dueSoonCount = (items ?? []).filter((item) => {
    if (item.due_at === null || item.status === 'approved') return false;
    const remaining = new Date(item.due_at).getTime() - Date.now();
    return remaining >= 0 && remaining <= 86_400_000;
  }).length;

  const savedViews: { id: SavedView; label: string; count: number }[] = [
    { id: 'all', label: l(S.all), count: (items ?? []).length },
    { id: 'risk', label: l(S.slaRiskView), count: overdueCount },
    { id: 'mine', label: l(S.mineView), count: mineCount },
    { id: 'unassigned', label: l(S.unassignedView), count: unassignedCount },
  ];

  const columns: DataTableColumn<QueueItem>[] = [
    {
      id: 'reference',
      header: l(S.reference),
      sortable: true,
      value: (item) => item.reference,
      render: (item) => (
        <div>
          <div className="font-mono text-xs font-bold" dir="ltr">{item.reference}</div>
          <div className="mt-0.5 max-w-64 truncate text-xs text-ink-soft">{l(item.service_name)}</div>
        </div>
      ),
    },
    {
      id: 'stage',
      header: l(S.stage),
      sortable: true,
      value: (item) => item.stage,
      render: (item) => <span className="font-semibold">{item.stage.replaceAll('_', ' ')}</span>,
    },
    {
      id: 'priority',
      header: l(S.priority),
      sortable: true,
      value: (item) => item.priority_score ?? item.housing_priority?.score ?? 0,
      render: (item) => {
        const score = item.priority_score ?? item.housing_priority?.score ?? null;
        const band = item.housing_priority?.band ?? null;
        if (score == null) {
          return <span className="text-ink-faint">—</span>;
        }
        const urgent = band === 'urgent' || score >= 80;
        return (
          <span className={`font-mono text-xs font-bold ${urgent ? 'text-reject' : 'text-ink'}`}>
            {score}
            {band ? (
              <span className="ms-1 block text-[10px] font-semibold uppercase text-ink-soft">
                {band}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: l(S.status),
      sortable: true,
      value: (item) => item.status,
      render: (item) => (
        <StatusPill state={item.status} label={l(STATUS_LABEL[item.status] ?? { en: item.status, ar: item.status })} />
      ),
    },
    {
      id: 'due',
      header: l(S.due),
      sortable: true,
      value: (item) => item.due_at,
      render: (item) => {
        const overdue = item.due_at !== null && new Date(item.due_at) < new Date() && item.status !== 'approved';
        return item.due_at ? (
          <span className={overdue ? 'font-bold text-reject' : 'font-semibold'}>
            {formatDate(item.due_at, lang)}
            {overdue && <span className="ms-1 block text-[10px] uppercase">{l(S.overdue)}</span>}
          </span>
        ) : <span aria-label={l({ en: 'No due date', ar: 'بدون موعد استحقاق' })}>—</span>;
      },
    },
    {
      id: 'channel',
      header: l(S.channel),
      sortable: true,
      value: (item) => item.channel,
      render: (item) => (
        <div>
          <span className="font-semibold">{l(channelLabel(item.channel))}</span>
          <span className="ms-2 rounded bg-aeblack-50 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase">{item.lang}</span>
        </div>
      ),
    },
    {
      id: 'action',
      header: '',
      align: 'end',
      render: (item) => (
        <span className="queue-row-actions inline-flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/cases/${item.reference}`);
            }}
            aria-label={`${l(S.openCase)} ${item.reference}`}
            className="inline-flex min-h-8 cursor-pointer items-center rounded-lg border border-aeblack-200 bg-white px-2.5 text-[11px] font-extrabold text-oasis shadow-sm transition hover:bg-seablue-50"
          >
            {l(S.openAction)}
          </button>
          <span className="text-xs font-bold text-gold-deep">{l(S.openCase)} →</span>
        </span>
      ),
    },
  ];

  return (
    <div>
      <HeroBand
        eyebrow={l(S.navOperations)}
        title={l(S.queues)}
        subtitle={l({
          en: 'Housing and review work ordered by household priority score, then SLA due date. Open a case to review evidence, eligibility and decision history.',
          ar: 'طلبات الإسكان والمراجعة مرتبة بدرجة أولوية الأسرة ثم موعد اتفاقية الخدمة. افتح الحالة لمراجعة الأدلة والأهلية وسجل القرار.',
        })}
        metrics={
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricTile
              dark
              label={l(COPY.openCases)}
              value={items === null ? '—' : openCount}
              hint={l(COPY.openCasesHint)}
              accent="#0173ab"
            />
            <MetricTile
              dark
              label={l(S.overdue)}
              value={items === null ? '—' : overdueCount}
              hint={l(COPY.overdueHint)}
              accent={overdueCount > 0 ? '#d83731' : '#3f8e50'}
            />
            <MetricTile
              dark
              label={l(COPY.dueSoon)}
              value={items === null ? '—' : dueSoonCount}
              hint={l(COPY.dueSoonHint)}
              accent={dueSoonCount > 0 ? '#d67907' : '#3f8e50'}
            />
          </div>
        }
      />

      {/* Saved views (plan point 24): client-side chips with live count badges. */}
      <div className="mb-3 flex flex-wrap items-center gap-2" role="group" aria-label={l(S.savedViews)}>
        {savedViews.map((savedView) => (
          <Chip key={savedView.id} size="sm" active={view === savedView.id} onClick={() => setView(savedView.id)}>
            {savedView.label}
            <span
              className={`rounded-full px-1.5 py-0.5 font-mono text-[9px] font-bold ${
                view === savedView.id ? 'bg-aeblack-800 text-white' : 'bg-aeblack-100 text-ink-soft'
              }`}
            >
              {savedView.count}
            </span>
          </Chip>
        ))}
      </div>

      <FilterBar summary={`${filtered.length} ${l(S.rows)}`}>
        <GlassInput
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={l({ en: 'Search reference or service…', ar: 'ابحث بالرقم أو الخدمة…' })}
          aria-label={l({ en: 'Search queue', ar: 'البحث في القائمة' })}
          className="w-full sm:w-72"
        />
        {STATUS_FILTERS.map((filter) => (
          <Chip key={filter || 'all'} size="sm" active={status === filter} onClick={() => setStatus(filter)}>
            {filter === '' ? l(S.all) : l(STATUS_LABEL[filter]!)}
          </Chip>
        ))}
        {stageFilter && (
          <Chip size="sm" active onClick={() => setStageFilter('')}>
            {l(S.queueFilterLabel)}: {stageFilter.replaceAll('_', ' ')} ✕
          </Chip>
        )}
        <span className="ms-auto inline-flex items-center gap-1" role="group" aria-label={l(S.densityLabel)}>
          <Chip size="sm" active={density === 'comfort'} onClick={() => applyDensity('comfort')}>
            {l(S.densityComfort)}
          </Chip>
          <Chip size="sm" active={density === 'compact'} onClick={() => applyDensity('compact')}>
            {l(S.densityCompact)}
          </Chip>
        </span>
      </FilterBar>

      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>}
      {!error && (
        <div className={`queue-table ${density === 'compact' ? 'density-compact' : ''}`}>
          <style>{`
            .queue-table .queue-row-actions > button { opacity: 0; transition: opacity 200ms cubic-bezier(0.22, 1, 0.36, 1); }
            .queue-table tr:hover .queue-row-actions > button,
            .queue-table tr:focus-within .queue-row-actions > button { opacity: 1; }
            @media (hover: none) { .queue-table .queue-row-actions > button { opacity: 1; } }
            @media (prefers-reduced-motion: reduce) { .queue-table .queue-row-actions > button { transition: none; } }
            .density-compact td { padding-block: 0.25rem; }
          `}</style>
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={(item) => item.id}
            loading={items === null}
            emptyTitle={l(S.emptyQueue)}
            emptyHint={l(S.emptyQueueHint)}
            filename="review-queue.csv"
            onRowClick={(item) => navigate(`/cases/${item.reference}`)}
          />
        </div>
      )}
    </div>
  );
}
